import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageSEO from '@/components/base/PageSEO';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const WHATSAPP_NUMBER = '+923348872555';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`;
const PHONE_URL = `tel:${WHATSAPP_NUMBER}`;
const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function AboutPage() {
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollReveal();

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Integrity',
      desc: 'We operate with complete honesty. No hidden fees, no false promises, no shortcuts.',
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Client First',
      desc: 'Every recommendation is made with your best interest in mind, not our commission.',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      desc: 'We use modern marketing strategies and data-driven insights to find the best opportunities.',
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Partnership',
      desc: 'We build long-term relationships, supporting you before, during, and after your purchase.',
    },
  ];

  return (
    <div className="relative min-h-screen bg-ivory-50">
      <PageSEO
        title="About Sunshine Real Estate — Trusted Investment Partner in Pakistan"
        description="Learn about Sunshine Real Estate Marketing SMC (Pvt.) Ltd. — Pakistan's trusted real estate investment partner since 2019. Our mission, vision, core values, and expert team dedicated to helping investors find verified property opportunities."
        keywords="about Sunshine Real Estate, real estate investment Pakistan, property marketing company, trusted real estate advisors Islamabad, Sunshine Real Estate team"
        canonicalPath="/about"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Sunshine Real Estate — Trusted Investment Partner',
            description: 'Learn about Sunshine Real Estate Marketing SMC (Pvt.) Ltd. — Pakistan\'s trusted real estate investment partner since 2019.',
            url: `${SITE_URL}/about`,
            isPartOf: {
              '@type': 'WebSite',
              name: 'Sunshine Real Estate Marketing SMC (Pvt.) Ltd.',
              url: SITE_URL,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Sunshine Real Estate Marketing SMC (Pvt.) Ltd.',
            url: SITE_URL,
            telephone: WHATSAPP_NUMBER,
            foundingDate: '2019',
            areaServed: 'Islamabad, Pakistan',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Islamabad',
              addressCountry: 'PK',
            },
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: WHATSAPP_NUMBER,
              contactType: 'sales',
              areaServed: 'PK',
              availableLanguage: ['English', 'Urdu'],
            },
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <div className="relative h-[260px] md:h-[340px] flex items-center justify-center overflow-hidden pt-16 md:pt-20">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20professional%20real%20estate%20office%20team%20meeting%20with%20diverse%20professionals%20in%20Pakistan%20warm%20modern%20office%20interior%20nice%20lighting%20professional%20corporate&width=1920&height=500&seq=801&orientation=landscape"
          alt="About Sunshine Real Estate — Trusted Investment Partner Pakistan"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-charcoal-400/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-3">
            About Sunshine Real Estate
          </h1>
          <p className="text-sm md:text-base text-white/80 max-w-lg mx-auto">
            Pakistan's trusted partner for verified property investments since 2019.
          </p>
        </div>
      </div>

      <main className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
        {/* Company Story */}
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-6xl mx-auto mb-16 md:mb-24 ${
            isVisible ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <div>
            <span className="inline-block text-xs md:text-sm font-semibold text-brand-orange uppercase tracking-widest mb-3">
              Our Story
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-charcoal-300 mb-5">
              Building Trust, One Investment at a Time
            </h2>
            <p className="text-sm md:text-base text-charcoal-50 leading-relaxed mb-4">
              Founded in 2019, Sunshine Real Estate Marketing SMC (Pvt.) Ltd. was born from a simple belief: Pakistani investors deserve better. Better transparency, better guidance, and better returns. In a market crowded with brokers, we chose to be something different — a true investment partner.
            </p>
            <p className="text-sm md:text-base text-charcoal-50 leading-relaxed mb-4">
              Starting with just a handful of verified projects in Islamabad, we have grown into one of Pakistan's most respected real estate marketing firms, trusted by over 1,200 investors locally and internationally.
            </p>
            <p className="text-sm md:text-base text-charcoal-50 leading-relaxed mb-6">
              Our team of certified advisors, legal experts, and marketing professionals work collaboratively to ensure every client receives world-class service, whether they are buying their first plot or expanding a commercial portfolio.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { navigate('/projects'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="px-5 py-2.5 bg-brand-orange text-white text-sm font-semibold rounded-lg hover:bg-brand-orangeLight transition-colors"
              >
                Explore Projects
              </button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 border border-charcoal-200 text-charcoal-300 text-sm font-semibold rounded-lg hover:bg-ivory-100 transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>

          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=premium%20modern%20real%20estate%20consultation%20meeting%20with%20professional%20advisor%20showing%20property%20plans%20to%20client%20warm%20lighting%20modern%20office%20Pakistan&width=700&height=500&seq=802&orientation=landscape"
              alt="Our Team — Sunshine Real Estate Pakistan"
              className="w-full h-72 md:h-80 object-cover object-top rounded-2xl shadow-xl"
            />
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg border border-ivory-200">
              <div className="text-2xl font-extrabold text-brand-orange">1,200+</div>
              <div className="text-xs text-charcoal-50">Happy Investors</div>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto mb-16 md:mb-24">
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-ivory-200">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange mb-4">
              <i className="ri-focus-3-line text-2xl" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-charcoal-300 mb-3">Our Mission</h3>
            <p className="text-sm md:text-base text-charcoal-50 leading-relaxed">
              To democratize access to verified, high-return real estate investments in Pakistan by combining expert market knowledge, transparent processes, and personalized client service — ensuring every investor makes informed, confident decisions.
            </p>
          </div>
          <div className="bg-charcoal-300 rounded-2xl p-6 md:p-8">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-orange/20 text-brand-orange mb-4">
              <i className="ri-eye-line text-2xl" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white mb-3">Our Vision</h3>
            <p className="text-sm md:text-base text-ivory-300 leading-relaxed">
              To become Pakistan's most trusted real estate investment brand — recognized globally by overseas Pakistanis and local investors alike for integrity, expertise, and consistent delivery of premium property opportunities with unmatched client satisfaction.
            </p>
          </div>
        </div>

        {/* Core Values */}
        <div className="max-w-6xl mx-auto mb-16 md:mb-24">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block text-xs md:text-sm font-semibold text-brand-orange uppercase tracking-widest mb-3">
              What Drives Us
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-charcoal-300">
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-md transition-all duration-300 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange mx-auto mb-4">
                  <i className={`${v.icon} text-2xl`} />
                </div>
                <h3 className="text-base font-bold text-charcoal-300 mb-2">{v.title}</h3>
                <p className="text-sm text-charcoal-50 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why Investors Choose */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block text-xs md:text-sm font-semibold text-brand-orange uppercase tracking-widest mb-3">
              The Sunshine Difference
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-charcoal-300">
              Why Investors Choose Sunshine
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { num: '01', title: 'Verified Projects Only', desc: 'We rigorously verify every project before listing. All NOCs, legal documents, and development status are confirmed by our legal team.' },
              { num: '02', title: 'Expert Guidance', desc: 'Our advisors bring years of market expertise. We do not just sell — we counsel, analyze, and guide you toward the right investment.' },
              { num: '03', title: 'End-to-End Support', desc: 'From the first consultation to final documentation and beyond, we handle everything so you can invest with complete peace of mind.' },
            ].map((item) => (
              <div key={item.num} className="bg-white rounded-2xl p-5 md:p-6 shadow-sm border-l-4 border-brand-orange">
                <div className="text-3xl font-extrabold text-brand-orange/20 mb-2">{item.num}</div>
                <h3 className="text-base md:text-lg font-bold text-charcoal-300 mb-2">{item.title}</h3>
                <p className="text-sm text-charcoal-50 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}